import { useEffect, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import { useToast } from '@tradener/lumen';
import { DateTime } from 'luxon';

import CREDIT_COUNTERPART_RATING_MATRIX, {
  CounterpartRatingMatrixYearProps,
  CounterpartRatingMatrixResponseProps,
  ResponseProps,
} from '@graphql/query/operationLimits/credit/counterpartRatingMatrix';

import { calculateDefaultCompetenceRange, parseCompetence } from '../../hooks/useOperationLimitsCompetenceFilters';
import {
  NINEBOX_YEARS,
  NineBoxCellToneProps,
  NineBoxMetricDataProps,
  NineBoxMetricProps,
  NineBoxRatingProps,
} from '../components/NineBox/mockData';

export interface UseOperationLimitsRatingMatrixProps {
  metric: NineBoxMetricProps;
}

export interface UseOperationLimitsRatingMatrixReturnProps {
  years: number[];
  metricData?: NineBoxMetricDataProps;
  isLoading: boolean;
  hasData: boolean;
  consideredPeriodLabel?: string;
}

type CounterpartRatingMatrixMetricKey = Exclude<keyof CounterpartRatingMatrixResponseProps, 'lastCompetence'>;

const METRIC_RESPONSE_MAP: Record<NineBoxMetricProps, CounterpartRatingMatrixMetricKey> = {
  R$: 'currency',
  MWm: 'mwm',
  '%': 'percent',
};

const RATING_FIELD_MAP: Record<NineBoxRatingProps, keyof CounterpartRatingMatrixYearProps> = {
  'A+': 'ratingAPlus',
  A: 'ratingA',
  B: 'ratingB',
  C: 'ratingC',
  'S/D': 'withoutRating',
  TOTAL: 'total',
};

const NON_TOTAL_RATINGS: NineBoxRatingProps[] = ['A+', 'A', 'B', 'C', 'S/D'];

const CURRENCY_HEATMAP_TONES: NineBoxCellToneProps[] = [
  'red-3',
  'red-2',
  'red-1',
  'yellow-3',
  'yellow-2',
  'yellow-1',
  'green-3',
  'green-2',
  'green-1',
];

interface ValueBoundsProps {
  min: number;
  max: number;
}

const formatNumber = (value: number, maximumFractionDigits = 0) =>
  Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(value);

const formatCurrencyValue = (value: number) => formatNumber(value, 2);
const formatMwmValue = (value: number) => formatNumber(value, 2);
const formatPercentValue = (value: number) => `${formatNumber(value, 2)}%`;

const resolveLabel = (metric: NineBoxMetricProps, value: number) => {
  if (metric === 'MWm') {
    return formatMwmValue(value);
  }

  if (metric === '%') {
    return formatPercentValue(value);
  }

  return formatCurrencyValue(value);
};

const resolveValueBounds = (rows: CounterpartRatingMatrixYearProps[]): ValueBoundsProps | undefined => {
  const values = rows.flatMap((row) =>
    NON_TOTAL_RATINGS.map((rating) => row[RATING_FIELD_MAP[rating]]).filter((value): value is number => value !== null),
  );

  if (values.length === 0) {
    return undefined;
  }

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
};

const resolveToneByScale = (value: number, tones: NineBoxCellToneProps[], bounds?: ValueBoundsProps): NineBoxCellToneProps => {
  if (!bounds || bounds.min === bounds.max) {
    return tones[Math.floor(tones.length / 2)];
  }

  const normalizedValue = (value - bounds.min) / (bounds.max - bounds.min);
  const clampedValue = Math.min(1, Math.max(0, normalizedValue));
  const toneIndex = Math.round(clampedValue * (tones.length - 1));

  return tones[toneIndex];
};

const resolveToneByProgress = (progress: number, tones: [NineBoxCellToneProps, NineBoxCellToneProps, NineBoxCellToneProps]) => {
  const clampedProgress = Math.min(1, Math.max(0, progress));
  const toneIndex = Math.round(clampedProgress * (tones.length - 1));

  return tones[toneIndex];
};

const resolveToneByRatingPercentage = (rating: NineBoxRatingProps, percentage: number): NineBoxCellToneProps => {
  if (rating === 'TOTAL') {
    return 'neutral';
  }

  if (rating === 'A+') {
    if (percentage <= 30) {
      return resolveToneByProgress(percentage / 30, ['red-3', 'red-2', 'red-1']);
    }

    if (percentage <= 40) {
      return resolveToneByProgress((percentage - 30) / 10, ['yellow-3', 'yellow-2', 'yellow-1']);
    }

    return resolveToneByProgress((percentage - 40) / 40, ['green-3', 'green-2', 'green-1']);
  }

  if (rating === 'A') {
    if (percentage <= 30) {
      return resolveToneByProgress(percentage / 30, ['green-1', 'green-2', 'green-3']);
    }

    return resolveToneByProgress((percentage - 30) / 30, ['red-1', 'red-2', 'red-3']);
  }

  if (rating === 'B') {
    if (percentage <= 15) {
      return resolveToneByProgress(percentage / 15, ['green-1', 'green-2', 'green-3']);
    }

    return resolveToneByProgress((percentage - 15) / 15, ['red-1', 'red-2', 'red-3']);
  }

  if (rating === 'C') {
    if (percentage <= 5) {
      return resolveToneByProgress(percentage / 5, ['green-1', 'green-2', 'green-3']);
    }

    return resolveToneByProgress((percentage - 5) / 15, ['red-1', 'red-2', 'red-3']);
  }

  if (rating === 'S/D') {
    if (percentage <= 0) {
      return 'green-1';
    }

    return resolveToneByProgress(percentage / 20, ['red-1', 'red-2', 'red-3']);
  }

  return 'zero';
};

const resolveTone = (
  value: number,
  metric: NineBoxMetricProps,
  rating: NineBoxRatingProps,
  bounds?: ValueBoundsProps,
): NineBoxCellToneProps => {
  if (rating === 'TOTAL') {
    return 'neutral';
  }

  if (metric === 'R$') {
    return resolveToneByScale(value, CURRENCY_HEATMAP_TONES, bounds);
  }

  return resolveToneByRatingPercentage(rating, value);
};

const buildYearsFromCompetenceRange = (startCompetence?: string | null, endCompetence?: string | null) => {
  const start = parseCompetence(startCompetence || undefined);
  const end = parseCompetence(endCompetence || undefined);

  if (!start || !end || start > end) {
    return undefined;
  }

  const yearCount = end.year - start.year + 1;

  if (yearCount <= 0) {
    return undefined;
  }

  return Array.from({ length: yearCount }, (_, index) => start.year + index);
};

const formatCompetenceLabel = (value: DateTime) => {
  const formatted = value.setLocale('pt-BR').toFormat('LLL/yyyy').replace(/\./g, '');

  return `${formatted.charAt(0).toUpperCase()}${formatted.slice(1)}`;
};

const buildConsideredPeriodLabel = (lastCompetence?: string | null) => {
  const endCompetence = parseCompetence(lastCompetence || undefined);

  if (!endCompetence) {
    return undefined;
  }

  const defaultRange = calculateDefaultCompetenceRange();
  const startCompetence = DateTime.fromObject({
    year: defaultRange.startYear,
    month: defaultRange.startMonth,
  });

  if (!startCompetence.isValid) {
    return undefined;
  }

  return `${formatCompetenceLabel(startCompetence)} - ${formatCompetenceLabel(endCompetence)}`;
};

const createEmptyMatrix = (years: number[]): NineBoxMetricDataProps =>
  ({
    'A+': Object.fromEntries(years.map((year) => [year, { label: '-', tone: 'zero' }])),
    A: Object.fromEntries(years.map((year) => [year, { label: '-', tone: 'zero' }])),
    B: Object.fromEntries(years.map((year) => [year, { label: '-', tone: 'zero' }])),
    C: Object.fromEntries(years.map((year) => [year, { label: '-', tone: 'zero' }])),
    'S/D': Object.fromEntries(years.map((year) => [year, { label: '-', tone: 'zero' }])),
    TOTAL: Object.fromEntries(years.map((year) => [year, { label: '-', tone: 'neutral' }])),
  } as NineBoxMetricDataProps);

const resolveValueByRating = (row: CounterpartRatingMatrixYearProps, rating: NineBoxRatingProps) => {
  if (rating === 'A+') {
    return row.ratingAPlus;
  }

  if (rating === 'A') {
    return row.ratingA;
  }

  if (rating === 'B') {
    return row.ratingB;
  }

  if (rating === 'C') {
    return row.ratingC;
  }

  if (rating === 'S/D') {
    return row.withoutRating;
  }

  return row.total;
};

const buildMetricData = (
  rows: CounterpartRatingMatrixYearProps[],
  years: number[],
  metric: NineBoxMetricProps,
  percentageRowsByYear?: Record<number, CounterpartRatingMatrixYearProps>,
): NineBoxMetricDataProps => {
  const data = createEmptyMatrix(years);
  const valueBounds = metric === 'R$' ? resolveValueBounds(rows) : undefined;

  rows.forEach((row) => {
    const valuesByRating = {
      'A+': row.ratingAPlus,
      A: row.ratingA,
      B: row.ratingB,
      C: row.ratingC,
      'S/D': row.withoutRating,
      TOTAL: row.total,
    };

    (Object.keys(RATING_FIELD_MAP) as NineBoxRatingProps[]).forEach((rating) => {
      const rawValue = valuesByRating[rating] ?? 0;

      if (metric === 'MWm') {
        const percentageRow = percentageRowsByYear?.[row.year];
        const percentageValue = percentageRow ? resolveValueByRating(percentageRow, rating) ?? 0 : 0;

        data[rating][row.year] = {
          label: resolveLabel(metric, rawValue),
          tone: resolveTone(percentageValue, '%', rating),
        };

        return;
      }

      data[rating][row.year] = {
        label: resolveLabel(metric, rawValue),
        tone: resolveTone(rawValue, metric, rating, valueBounds),
      };
    });
  });

  return data;
};

function useOperationLimitsRatingMatrix({
  metric,
}: UseOperationLimitsRatingMatrixProps): UseOperationLimitsRatingMatrixReturnProps {
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const { data, error, loading } = useQuery<ResponseProps>(CREDIT_COUNTERPART_RATING_MATRIX, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const selectedMetric = METRIC_RESPONSE_MAP[metric];
  const rows = data?.creditCounterpartRatingMatrix?.[selectedMetric]?.yearlyValues;
  const lastCompetence = data?.creditCounterpartRatingMatrix?.lastCompetence;
  const percentageRowsByYear = useMemo(
    () =>
      Object.fromEntries(
        (data?.creditCounterpartRatingMatrix?.percent?.yearlyValues || []).map((row) => [row.year, row]),
      ) as Record<number, CounterpartRatingMatrixYearProps>,
    [data?.creditCounterpartRatingMatrix?.percent?.yearlyValues],
  );
  const hasData = Boolean(rows?.length);

  useEffect(() => {
    if (!error) {
      return;
    }

    toast({
      title: 'Erro',
      status: 'error',
      description: error.message || 'Erro ao carregar matriz de rating.',
    });
  }, [error, toast]);

  const years = useMemo(() => {
    const backendYears = rows?.map(({ year }) => year) || [];
    const rangeYears = buildYearsFromCompetenceRange(searchParams.get('startCompetence'), searchParams.get('endCompetence'));

    if (backendYears.length > 0) {
      return backendYears;
    }

    if (rangeYears?.length) {
      return rangeYears;
    }

    return NINEBOX_YEARS;
  }, [rows, searchParams]);

  const metricData = useMemo(() => {
    if (!rows || rows.length === 0) {
      return undefined;
    }

    return buildMetricData(rows, years, metric, percentageRowsByYear);
  }, [rows, metric, years, percentageRowsByYear]);

  const consideredPeriodLabel = useMemo(() => buildConsideredPeriodLabel(lastCompetence), [lastCompetence]);

  return {
    years,
    metricData,
    isLoading: loading,
    hasData,
    consideredPeriodLabel,
  };
}

export default useOperationLimitsRatingMatrix;
